/**
 * iter279 — ProductCardModal: ürün özet kartı.
 * Ürün adına tıklanınca açılır. Formüller + üretim geçmişi + bitmiş lotlar
 * ile teslimat özeti tek pencerede görüntülenir.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, PROD_STATUS_LABEL } from "@/lib/api";
import { Modal, Btn, Badge } from "@/components/UI";
import { Package, Factory, PackageCheck, FlaskConical, Truck, Info } from "lucide-react";

export default function ProductCardModal({ productId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!productId) return;
    setData(null); setErr(null);
    api.get(`/products/${productId}/card`)
      .then(setData)
      .catch(e => setErr(e.response?.data?.detail || "Ürün kartı yüklenemedi"));
  }, [productId]);

  return (
    <Modal open={!!productId} onClose={onClose}
           title={data?.product?.name ? `Ürün Kartı — ${data.product.name}` : "Ürün Kartı"}
           size="full">
      {err && <div className="text-red-600 text-sm p-4" data-testid="pc-error">{err}</div>}
      {!data && !err && <div className="py-8 text-center text-slate-400 text-sm">Yükleniyor…</div>}
      {data && (
        <div className="space-y-4" data-testid="product-card-content">
          {/* Header meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <MetaCell label="Müşteri" val={data.product.customer_name || "—"}/>
            <MetaCell label="Marka" val={data.product.brand_name || "—"}/>
            <MetaCell label="Dolum" val={`${data.product.fill_qty || "—"} ${data.product.fill_unit || ""}`}/>
            <MetaCell label="Karton/Adet" val={data.product.units_per_carton || "—"}/>
            <MetaCell label="Dayanım (gün)" val={data.product.shelf_life_days || "—"}/>
            <MetaCell label="Barkod" val={data.product.barcode || "—"} mono/>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <StatCard color="violet" icon={FlaskConical} label="Formül" v1={data.stats.total_formulas} v2={`${data.stats.active_formulas} aktif`}/>
            <StatCard color="orange" icon={Factory} label="Üretim Emri" v1={data.stats.total_production_orders} v2={`${data.stats.completed_production_orders} tamamlandı`}/>
            <StatCard color="emerald" icon={PackageCheck} label="Üretilen" v1={fmt(data.stats.total_produced)} v2={`${(data.finished_lots || []).length} lot`}/>
            <StatCard color="rose" icon={Truck} label="Teslim Edilen" v1={fmt(data.stats.total_delivered)} v2=""/>
            <StatCard color="sky" icon={Package} label="Stokta" v1={fmt(data.stats.in_stock)} v2=""/>
          </div>

          {/* Formulas */}
          <Section title="Formüller" icon={FlaskConical} count={data.formulas.length} testid="pc-formulas">
            {data.formulas.length === 0 ? <Empty/> : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["Proje No","Rev","Durum","Oluşturan","Tarih",""].map((h,i)=><th key={i} className="text-left px-2 py-1.5 text-slate-600 uppercase text-[10px]">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.formulas.map(f => (
                    <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-mono text-indigo-700">{f.project_no || "—"}</td>
                      <td className="px-2 py-1.5">{f.revision || "R00"} {f.is_active_revision && <span className="text-[9px] bg-emerald-600 text-white rounded px-1 ml-1">AKTİF</span>}</td>
                      <td className="px-2 py-1.5">{f.status}</td>
                      <td className="px-2 py-1.5">{f.created_by_name || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{(f.created_at || "").slice(0,10)}</td>
                      <td className="px-2 py-1.5"><Link to="/formulas" onClick={onClose} className="text-indigo-700 hover:underline text-[10px]">Aç →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Production */}
          <Section title="Üretim Geçmişi" icon={Factory} count={data.production_orders.length} testid="pc-production">
            {data.production_orders.length === 0 ? <Empty/> : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["Üretim No","SO","Durum","Öncelik","Planlanan","Batch","Tarih",""].map((h,i)=><th key={i} className="text-left px-2 py-1.5 text-slate-600 uppercase text-[10px]">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.production_orders.map(p => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-mono text-orange-700">{p.prod_no}</td>
                      <td className="px-2 py-1.5 font-mono text-indigo-700">{p.so_no || "—"}</td>
                      <td className="px-2 py-1.5"><Badge level={p.final_approved_at ? "ok" : "attention"}>{PROD_STATUS_LABEL?.[p.status] || p.status}</Badge></td>
                      <td className="px-2 py-1.5 text-[10px]">{p.priority || "—"}</td>
                      <td className="px-2 py-1.5 font-mono">{p.planned_units || "—"} adet</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{p.planned_batch_kg || "—"} kg</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{(p.created_at || "").slice(0,10)}</td>
                      <td className="px-2 py-1.5"><Link to={`/production/${p.id}`} onClick={onClose} className="text-orange-700 hover:underline text-[10px]">Aç →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Finished lots */}
          <Section title="Bitmiş Ürün Lotları / Teslimat" icon={PackageCheck} count={data.finished_lots.length} testid="pc-finished">
            {data.finished_lots.length === 0 ? <Empty/> : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["Lot","Üretilen","Stokta","Teslim","SKT","Durum","Tarih"].map((h,i)=><th key={i} className="text-left px-2 py-1.5 text-slate-600 uppercase text-[10px]">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.finished_lots.map(fl => {
                    const delivered = Math.max(0, (fl.qty_produced || 0) - (fl.qty_available || 0));
                    return (
                      <tr key={fl.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-2 py-1.5 font-mono text-emerald-700">{fl.lot_code}</td>
                        <td className="px-2 py-1.5 font-mono">{fmt(fl.qty_produced)}</td>
                        <td className="px-2 py-1.5 font-mono text-sky-700">{fmt(fl.qty_available)}</td>
                        <td className="px-2 py-1.5 font-mono text-rose-700">{fmt(delivered)}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{fl.expiry_date || "—"}</td>
                        <td className="px-2 py-1.5 text-[10px]">{fl.status}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{(fl.created_at || "").slice(0,10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}
      <div className="flex justify-end mt-3">
        <Btn variant="secondary" onClick={onClose} data-testid="product-card-close">Kapat</Btn>
      </div>
    </Modal>
  );
}

const fmt = (v) => Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });

const MetaCell = ({ label, val, mono }) => (
  <div className="bg-slate-50 rounded p-2">
    <div className="text-[9px] uppercase text-slate-500">{label}</div>
    <div className={`text-xs font-semibold text-slate-800 ${mono ? "font-mono" : ""}`}>{val}</div>
  </div>
);

const STAT_COLORS = {
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  orange: "bg-orange-50 border-orange-200 text-orange-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
};
const StatCard = ({ color, icon: Icon, label, v1, v2 }) => (
  <div className={`border rounded p-2 ${STAT_COLORS[color]}`}>
    <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider">
      <Icon size={11}/> {label}
    </div>
    <div className="text-xl font-bold font-mono">{v1}</div>
    <div className="text-[10px] opacity-80">{v2}</div>
  </div>
);

const Section = ({ title, icon: Icon, count, testid, children }) => (
  <div className="border border-slate-200 rounded" data-testid={testid}>
    <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center gap-2">
      <Icon size={13} className="text-slate-600"/>
      <span className="text-xs font-semibold text-slate-700">{title}</span>
      <span className="text-[10px] bg-white border rounded-full px-1.5 text-slate-500 font-mono">{count}</span>
    </div>
    <div className="max-h-64 overflow-y-auto">{children}</div>
  </div>
);

const Empty = () => <div className="py-6 text-center text-xs text-slate-400 italic">Kayıt yok.</div>;
